// apps/web/src/pages/vendas/use-pv-comandos.ts — Fase 12, Plano 23 (PV-01).
// Cliente dos comandos de escrita do PV (mesmo formato de use-kanban-comandos.ts).
// A autorização real fica no servidor (pv:editar); 409 PV_VERSAO_DESATUALIZADA vira `conflito`.
import { useCallback } from 'react';
import type {
  AjustarItemRecebidoInput,
  AnexarItensPvInput,
  AtualizarCabecalhoPvInput,
  CriarPedidoVendaInput,
  EditarItemPvInput,
  MotivoVersaoInput,
  PedidoVendaView,
  TrocarMaterialItemInput,
  VincularLotesPvInput,
} from '@pieltec/contracts';
import { apiMutate } from '../../lib/csrf-fetch';
import { interpretarErroApi, MENSAGEM_FALHA_PV, MENSAGEM_SEM_PERMISSAO_PV } from '../../lib/vendas/erro-api';

export const CODIGO_VERSAO_DESATUALIZADA = 'PV_VERSAO_DESATUALIZADA';
export const MENSAGEM_VERSAO_DESATUALIZADA = 'Este PV foi alterado por outro usuário. Recarregue para ver as mudanças antes de salvar.';
// Textos únicos em lib/vendas/erro-api (WR-D1-12); reexportados por compatibilidade.
export { MENSAGEM_FALHA_PV, MENSAGEM_SEM_PERMISSAO_PV };

export type ResultadoComandoPv =
  | { readonly tipo: 'ok'; readonly resultado: PedidoVendaView }
  | { readonly tipo: 'conflito'; readonly codigo: string; readonly message: string }
  | { readonly tipo: 'erro'; readonly codigo?: string; readonly message: string };

export async function interpretar(resposta: Response): Promise<ResultadoComandoPv> {
  if (resposta.ok) {
    return { tipo: 'ok', resultado: (await resposta.json()) as PedidoVendaView };
  }

  let corpo: unknown = null;
  try {
    corpo = await resposta.json();
  } catch {
    // Corpo vazio ou não-JSON: a UI mostra a mensagem genérica como texto.
  }
  const erro = interpretarErroApi(resposta.status, corpo, MENSAGEM_FALHA_PV);
  const codigo = erro.codigo ?? undefined;

  if (resposta.status === 409 && codigo === CODIGO_VERSAO_DESATUALIZADA) {
    return { tipo: 'conflito', codigo, message: MENSAGEM_VERSAO_DESATUALIZADA };
  }
  return { tipo: 'erro', ...(codigo && resposta.status !== 403 ? { codigo } : {}), message: erro.mensagem };
}

export async function enviar(path: string, method: string, body: unknown): Promise<ResultadoComandoPv> {
  try {
    const resposta = await apiMutate(path, { method, body });
    return await interpretar(resposta);
  } catch {
    return { tipo: 'erro', message: MENSAGEM_FALHA_PV };
  }
}

/** Caminho de uma rota de item do PV (`/itens/:itemId{sufixo}`). */
export function caminhoItemPv(pedidoId: string, itemId: string, sufixo = ''): string {
  return `/api/v1/vendas/pedidos/${encodeURIComponent(pedidoId)}/itens/${encodeURIComponent(itemId)}${sufixo}`;
}

export function usePvComandos() {
  const criarPedido = useCallback(
    (corpo: CriarPedidoVendaInput): Promise<ResultadoComandoPv> => enviar('/api/v1/vendas/pedidos', 'POST', corpo),
    [],
  );

  const atualizarCabecalho = useCallback(
    (pedidoId: string, corpo: AtualizarCabecalhoPvInput): Promise<ResultadoComandoPv> =>
      enviar(`/api/v1/vendas/pedidos/${encodeURIComponent(pedidoId)}`, 'PATCH', corpo),
    [],
  );

  const anexarItens = useCallback(
    (pedidoId: string, corpo: AnexarItensPvInput): Promise<ResultadoComandoPv> =>
      enviar(`/api/v1/vendas/pedidos/${encodeURIComponent(pedidoId)}/itens`, 'POST', corpo),
    [],
  );

  /** Aponta lotes existentes (sem PV) para itens do PV (12-11, pv:ajustar); 409 de versão vira `conflito`. */
  const vincularLotes = useCallback(
    (pedidoId: string, corpo: VincularLotesPvInput): Promise<ResultadoComandoPv> =>
      enviar(`/api/v1/vendas/pedidos/${encodeURIComponent(pedidoId)}/vincular-lotes`, 'POST', corpo),
    [],
  );

  /** Edita item AINDA NÃO RECEBIDO (12-12, pv:editar): `versao` é a do item; motivo obrigatório. */
  const editarItem = useCallback(
    (pedidoId: string, itemId: string, corpo: EditarItemPvInput): Promise<ResultadoComandoPv> =>
      enviar(caminhoItemPv(pedidoId, itemId), 'PATCH', corpo),
    [],
  );

  /** Cancela item AINDA NÃO RECEBIDO com motivo (12-12, pv:editar). */
  const cancelarItem = useCallback(
    (pedidoId: string, itemId: string, corpo: MotivoVersaoInput): Promise<ResultadoComandoPv> =>
      enviar(caminhoItemPv(pedidoId, itemId, '/cancelar'), 'POST', corpo),
    [],
  );

  /** Ajusta item JÁ RECEBIDO (12-30, pv:ajustar): quantidade, preço, prazo e prioridade; `versao` é a do item. */
  const ajustarItem = useCallback(
    (pedidoId: string, itemId: string, corpo: AjustarItemRecebidoInput): Promise<ResultadoComandoPv> =>
      enviar(caminhoItemPv(pedidoId, itemId, '/ajustar'), 'POST', corpo),
    [],
  );

  /** Preço da tabela vigente hoje, origem TABELA (12-30, pv:ajustar); 409 PV_SEM_PRECO_VIGENTE sem versão vigente. */
  const reprecificarItem = useCallback(
    (pedidoId: string, itemId: string, corpo: MotivoVersaoInput): Promise<ResultadoComandoPv> =>
      enviar(caminhoItemPv(pedidoId, itemId, '/reprecificar'), 'POST', corpo),
    [],
  );

  /** Troca de cor/material (12-30, D-29, pv:ajustar); 409 TROCA_MATERIAL_BLOQUEADA quando há etapa apontada. */
  const trocarMaterial = useCallback(
    (pedidoId: string, itemId: string, corpo: TrocarMaterialItemInput): Promise<ResultadoComandoPv> =>
      enviar(caminhoItemPv(pedidoId, itemId, '/trocar-material'), 'POST', corpo),
    [],
  );

  /** Encerra o saldo do item sem faturamento (12-30, D-26, pv:ajustar); 409 PV_SEM_SALDO sem saldo. */
  const encerrarSaldo = useCallback(
    (pedidoId: string, itemId: string, corpo: MotivoVersaoInput): Promise<ResultadoComandoPv> =>
      enviar(caminhoItemPv(pedidoId, itemId, '/encerrar-saldo'), 'POST', corpo),
    [],
  );

  /** Cancela o PV e os itens ativos (12-30, pv:editar); `versao` é a do PV; 409 PV_ITENS_RECEBIDOS com item recebido. */
  const cancelarPv = useCallback(
    (pedidoId: string, corpo: MotivoVersaoInput): Promise<ResultadoComandoPv> =>
      enviar(`/api/v1/vendas/pedidos/${encodeURIComponent(pedidoId)}/cancelar`, 'POST', corpo),
    [],
  );

  return {
    criarPedido,
    atualizarCabecalho,
    anexarItens,
    vincularLotes,
    editarItem,
    cancelarItem,
    ajustarItem,
    reprecificarItem,
    trocarMaterial,
    encerrarSaldo,
    cancelarPv,
  };
}
